import { FC, useState } from "react";
import {
  ErrorBoundaryPropsWithComponent,
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from "react-error-boundary";

import { appsignal } from "@/appsignal";
import { getEndpoint } from "@/lib/endpoint";

type ErrorBoundaryProps = Omit<
  ErrorBoundaryPropsWithComponent,
  "FallbackComponent"
> &
  Partial<Pick<ErrorBoundaryPropsWithComponent, "FallbackComponent">>;

export const ErrorBoundary: FC<ErrorBoundaryProps> = ({
  FallbackComponent = ErrorFallback,
  ...props
}) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={reportError}
      {...props}
    />
  );
};

export const ErrorFallback: FC<FallbackProps> = ({ error }) => {
  return (
    <div role="alert" className="rounded-md bg-red-200 p-4">
      <div className="text-lg font-bold text-red-900">
        Er is een fout opgetreden
      </div>
      <div>
        Dit gedeelte van de pagina kan wegens een systeemfout niet worden
        getoond. Probeer het opnieuw of neem contact op met de helpdesk.
      </div>
      <div>
        <div>url: {window.location.href}</div>
        <div>endpoint: {getEndpoint()}</div>
      </div>
      {error instanceof Error && <ErrorDetails error={error} />}
    </div>
  );
};

export const ErrorDetails: FC<{ error: Error }> = ({ error }) => {
  const [errorLines, setErrorLines] = useState<number | undefined>(5);

  return (
    <div className="mt-2 overflow-y-scroll border-l-4 border-red-500 pl-4">
      {error.message}

      {error.stack && (
        <div className="mt-2 whitespace-nowrap font-mono text-sm text-gray-500">
          {error.stack
            .split("\n")
            .slice(0, errorLines)
            .map((line, index) => (
              <div key={index} className="">
                {line}
              </div>
            ))}
          {errorLines && (
            <button
              className="border-2"
              onClick={() => setErrorLines(undefined)}
            >
              Toon alle
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function reportError(error: Error) {
  console.log("ErrorBoundary error", error);
  void appsignal.sendError(error, {});
}
